import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import {
    Box, Button, Typography, Container, CircularProgress, Paper, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    FormControl, InputLabel, Select, MenuItem as MuiMenuItem,
    List, ListItem, ListItemText, ListItemIcon, Divider, Tooltip,
    IconButton, Avatar, Switch, FormControlLabel, Badge, Tab, Tabs,
} from '@mui/material';
import MailIcon from '@mui/icons-material/Mail';
import DraftsIcon from '@mui/icons-material/Drafts';
import SendIcon from '@mui/icons-material/Send';
import InboxIcon from '@mui/icons-material/Inbox';
import GroupIcon from '@mui/icons-material/Group';
import SchoolIcon from '@mui/icons-material/School';
import ClassIcon from '@mui/icons-material/Class';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReplyIcon from '@mui/icons-material/Reply';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import EventIcon from '@mui/icons-material/Event';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import AuthContext from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

import { NAV_BG, AMBER, AMBER_DARK, AMBER_GLOW } from '../theme';
import EmptyState from '../components/EmptyState';

const RSVP_COLORS = {
    yes:   { bg: 'rgba(34,197,94,0.12)',  text: '#15803d', border: 'rgba(34,197,94,0.3)'  },
    no:    { bg: 'rgba(239,68,68,0.10)',  text: '#b91c1c', border: 'rgba(239,68,68,0.3)'  },
    maybe: { bg: 'rgba(245,158,11,0.12)', text: '#92400e', border: 'rgba(245,158,11,0.3)' },
    null:  { bg: 'rgba(148,163,184,0.1)', text: '#64748b', border: 'rgba(148,163,184,0.3)'},
};

function fmtDate(ts) {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
        + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function RsvpChip({ rsvp }) {
    const { t } = useTranslation();
    const c = RSVP_COLORS[rsvp] || RSVP_COLORS.null;
    const label = rsvp ? t(`messages.rsvp_${rsvp}`) : t('messages.rsvp_none');
    const icon = rsvp === 'yes' ? <CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />
               : rsvp === 'no'  ? <CancelOutlinedIcon    sx={{ fontSize: '14px !important' }} />
               : rsvp === 'maybe' ? <HelpOutlineIcon     sx={{ fontSize: '14px !important' }} />
               : null;
    return (
        <Chip size="small" icon={icon || undefined} label={label}
            sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', height: 22,
                  background: c.bg, color: c.text, border: `1px solid ${c.border}` }} />
    );
}

function RichTextEditor({ value, onChange }) {
    const editorRef = useRef(null);
    const execCmd = (cmd) => {
        editorRef.current.focus();
        document.execCommand(cmd, false, null);
        onChange(editorRef.current.innerHTML);
    };
    useEffect(() => {
        if (editorRef.current && editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value || '';
        }
    // eslint-disable-next-line
    }, []);
    const toolbarBtn = (title, icon, cmd) => (
        <Tooltip title={title}>
            <IconButton size="small" onMouseDown={(e) => { e.preventDefault(); execCmd(cmd); }}
                sx={{ color: '#475569', '&:hover': { color: AMBER } }}>
                {icon}
            </IconButton>
        </Tooltip>
    );
    return (
        <Box sx={{ border: '1px solid #cbd5e1', borderRadius: '8px', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.5, borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                {toolbarBtn('Bold',   <FormatBoldIcon fontSize="small" />,          'bold')}
                {toolbarBtn('Italic', <FormatItalicIcon fontSize="small" />,        'italic')}
                {toolbarBtn('Underline', <FormatUnderlinedIcon fontSize="small" />, 'underline')}
                {toolbarBtn('Bullet list', <FormatListBulletedIcon fontSize="small" />, 'insertUnorderedList')}
            </Box>
            <Box ref={editorRef} contentEditable suppressContentEditableWarning
                onInput={() => onChange(editorRef.current.innerHTML)}
                sx={{ minHeight: 140, p: 1.5, outline: 'none', fontFamily: '"Outfit", sans-serif',
                      fontSize: '0.9rem', lineHeight: 1.6, color: '#1e293b',
                      '& ul': { pl: 2.5, mt: 0.5, mb: 0.5 }, '& li': { mb: 0.25 } }}
            />
        </Box>
    );
}

/** Multi-child picker: search + chip list */
function MultiChildPicker({ classes, selectedIds, onChange }) {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const inputRef = useRef(null);

    const allChildren = classes.flatMap(sc =>
        sc.departments.flatMap(d =>
            (d.children || []).map(c => ({
                id: c.id || String(c.child_id),
                firstname: c.firstname,
                lastname: c.lastname,
                className: sc.name,
                deptName: d.name,
            }))
        )
    );

    const selectedSet = new Set(selectedIds);

    const suggestions = search.trim().length >= 2
        ? allChildren
            .filter(c => !selectedSet.has(c.id) &&
                `${c.firstname} ${c.lastname}`.toLowerCase().includes(search.toLowerCase()))
            .slice(0, 8)
        : [];

    const addChild = (child) => {
        onChange([...selectedIds, child.id]);
        setSearch('');
        inputRef.current?.focus();
    };

    const removeChild = (id) => onChange(selectedIds.filter(x => x !== id));

    const selectedChildren = allChildren.filter(c => selectedSet.has(c.id));

    return (
        <Box>
            {/* Selected chips */}
            {selectedChildren.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                    {selectedChildren.map(c => (
                        <Chip key={c.id}
                            label={`${c.firstname} ${c.lastname}`}
                            size="small"
                            onDelete={() => removeChild(c.id)}
                            deleteIcon={<CloseIcon />}
                            sx={{ fontFamily: '"Outfit", sans-serif', background: 'rgba(15,45,94,0.08)', color: NAV_BG,
                                  border: '1px solid rgba(15,45,94,0.2)', '& .MuiChip-deleteIcon': { fontSize: 14, color: '#64748b' } }}
                        />
                    ))}
                </Box>
            )}

            {/* Search input */}
            <Box sx={{ position: 'relative' }}>
                <TextField
                    inputRef={inputRef}
                    label={t('messages.search_child')}
                    fullWidth size="small"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: AMBER },
                          '& .MuiInputLabel-root.Mui-focused': { color: AMBER } }}
                    inputProps={{ style: { fontFamily: '"Outfit", sans-serif' } }}
                />
                {suggestions.length > 0 && (
                    <Paper elevation={4} sx={{ position: 'absolute', zIndex: 10, left: 0, right: 0, top: '100%', mt: 0.5, borderRadius: '8px', overflow: 'hidden', maxHeight: 240, overflowY: 'auto' }}>
                        {suggestions.map(c => (
                            <Box key={c.id} onClick={() => addChild(c)}
                                sx={{ px: 2, py: 1.25, cursor: 'pointer', '&:hover': { background: 'rgba(15,45,94,0.06)' } }}>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', fontWeight: 500 }}>
                                    {c.firstname} {c.lastname}
                                </Typography>
                                <Typography variant="caption" sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b' }}>
                                    {c.className} · {c.deptName}
                                </Typography>
                            </Box>
                        ))}
                    </Paper>
                )}
            </Box>
        </Box>
    );
}

export default function MessagesPage() {
    const { authTokens, logoutUser, backendUrl, pageTitle } = useContext(AuthContext);
    const { t } = useTranslation();
    document.title = pageTitle + ' - ' + t('messages.title');

    const [tab, setTab] = useState(0); // 0 = Received, 1 = Sent
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [groupDetail, setGroupDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Received messages (from parents)
    const [received, setReceived] = useState([]);
    const [openedMessage, setOpenedMessage] = useState(null);
    const [selectedReceived, setSelectedReceived] = useState(new Set());
    const [deleting, setDeleting] = useState(false);

    // Sent messages selection/deletion
    const [selectedSent, setSelectedSent] = useState(new Set());
    const [deletingSent, setDeletingSent] = useState(false);

    // Compose state
    const [composeOpen, setComposeOpen] = useState(false);
    const [targetType, setTargetType] = useState('all');
    const [targetId, setTargetId] = useState('');
    const [targetChildIds, setTargetChildIds] = useState([]);  // multi-child
    const [profileIds, setProfileIds] = useState([]);
    const [composeTitle, setComposeTitle] = useState('');
    const [composeText, setComposeText] = useState('');
    const [isInvitation, setIsInvitation] = useState(false);
    const [sending, setSending] = useState(false);
    const [selectedClass, setSelectedClass] = useState('');

    // Data
    const [classes, setClasses] = useState([]);
    const [routes, setRoutes] = useState([]);

    const authHeader = useCallback(() => ({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + String(authTokens.access),
    }), [authTokens]);

    const fetchGroups = useCallback(async () => {
        const res = await fetch(backendUrl + 'api/messages/', { headers: authHeader() });
        if (res.status === 401) { logoutUser(); return; }
        const data = await res.json();
        setGroups(data);
    }, [authHeader, backendUrl, logoutUser]);

    const fetchReceived = useCallback(async () => {
        const res = await fetch(backendUrl + 'api/messages/received/', { headers: authHeader() });
        if (res.status === 401) { logoutUser(); return; }
        if (res.ok) setReceived(await res.json());
    }, [authHeader, backendUrl, logoutUser]);

    const fetchClasses = useCallback(async () => {
        const res = await fetch(backendUrl + 'api/get-classes/', { headers: authHeader() });
        if (res.ok) setClasses(await res.json());
    }, [authHeader, backendUrl]);

    const fetchRoutes = useCallback(async () => {
        const res = await fetch(backendUrl + 'api/get-routes/', { headers: authHeader() });
        if (res.ok) setRoutes(await res.json());
    }, [authHeader, backendUrl]);

    useEffect(() => {
        Promise.all([fetchGroups(), fetchReceived(), fetchClasses(), fetchRoutes()]).finally(() => setLoading(false));
        const interval = setInterval(fetchReceived, 15000);
        return () => clearInterval(interval);
    }, []);

    const openReceivedMessage = async (msg) => {
        setOpenedMessage(msg);
        if (!msg.read) {
            await fetch(backendUrl + `api/messages/${msg.message_id}/read/`, { method: 'POST', headers: authHeader() });
            setReceived(prev => prev.map(m => m.message_id === msg.message_id ? { ...m, read: true } : m));
            window.dispatchEvent(new CustomEvent('messages-read'));
        }
    };

    const openGroup = async (group) => {
        setSelectedGroup(group);
        setDetailLoading(true);
        const res = await fetch(backendUrl + 'api/messages/' + group.group_id + '/', { headers: authHeader() });
        if (res.ok) setGroupDetail(await res.json());
        setDetailLoading(false);
    };

    const toggleSelectReceived = (id, e) => {
        e.stopPropagation();
        setSelectedReceived(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAllReceived = () => {
        if (selectedReceived.size === received.length) {
            setSelectedReceived(new Set());
        } else {
            setSelectedReceived(new Set(received.map(m => m.message_id)));
        }
    };

    const handleDeleteReceived = async () => {
        if (selectedReceived.size === 0) return;
        setDeleting(true);
        try {
            const res = await fetch(backendUrl + 'api/messages/received/delete/', {
                method: 'POST',
                headers: authHeader(),
                body: JSON.stringify({ message_ids: Array.from(selectedReceived) }),
            });
            if (res.ok) {
                setReceived(prev => prev.filter(m => !selectedReceived.has(m.message_id)));
                setSelectedReceived(new Set());
            }
        } finally {
            setDeleting(false);
        }
    };

    const toggleSelectSent = (id, e) => {
        e.stopPropagation();
        setSelectedSent(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAllSent = () => {
        if (selectedSent.size === groups.length) {
            setSelectedSent(new Set());
        } else {
            setSelectedSent(new Set(groups.map(g => g.group_id)));
        }
    };

    const handleDeleteSent = async () => {
        if (selectedSent.size === 0) return;
        setDeletingSent(true);
        try {
            const res = await fetch(backendUrl + 'api/messages/sent/delete/', {
                method: 'POST',
                headers: authHeader(),
                body: JSON.stringify({ group_ids: Array.from(selectedSent) }),
            });
            if (res.ok) {
                setGroups(prev => prev.filter(g => !selectedSent.has(g.group_id)));
                setSelectedSent(new Set());
            }
        } finally {
            setDeletingSent(false);
        }
    };

    const deptOptions = selectedClass
        ? (classes.find(c => String(c.id) === selectedClass)?.departments || [])
        : [];

    const resetCompose = () => {
        setComposeTitle(''); setComposeText(''); setTargetType('all');
        setTargetId(''); setTargetChildIds([]); setProfileIds([]);
        setSelectedClass(''); setIsInvitation(false);
    };

    const handleTargetTypeChange = (val) => {
        setTargetType(val); setTargetId(''); setSelectedClass(''); setTargetChildIds([]);
    };

    const sendDisabled = !composeText.trim() || sending
        || (targetType === 'department' && !targetId)
        || (targetType === 'schoolclass' && !targetId)
        || (targetType === 'route' && !targetId)
        || (targetType === 'children' && targetChildIds.length === 0);

    const handleSend = async () => {
        if (sendDisabled) return;
        setSending(true);
        try {
            const body = {
                title: composeTitle,
                text: composeText,
                target_type: targetType,
                target_id: targetId || undefined,
                target_ids: targetType === 'children' ? targetChildIds : undefined,
                profile_ids: profileIds,
                is_invitation: isInvitation,
            };
            const res = await fetch(backendUrl + 'api/send-message/', {
                method: 'POST', headers: authHeader(), body: JSON.stringify(body),
            });
            if (res.ok) {
                setComposeOpen(false);
                resetCompose();
                await fetchGroups();
            }
        } finally {
            setSending(false);
        }
    };

    const handleReply = (msg) => {
        const subject = msg.title ? `Re: ${msg.title.replace(/^(Re:\s*)+/i, '')}` : '';
        setComposeTitle(subject);
        setComposeText('');
        setIsInvitation(false);
        if (msg.from_children && msg.from_children.length > 0) {
            setTargetType('children');
            setTargetChildIds(msg.from_children.map(c => c.id));
        } else {
            setTargetType('all');
            setTargetChildIds([]);
        }
        setTargetId('');
        setSelectedClass('');
        setProfileIds([]);
        setOpenedMessage(null);
        setComposeOpen(true);
    };

    const unreadReceivedCount = received.filter(m => !m.read).length;

    if (loading) {
        return <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"><CircularProgress /></Box>;
    }

    return (
        <Container sx={{ mt: 10, mb: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {selectedGroup && (
                        <IconButton size="small" onClick={() => { setSelectedGroup(null); setGroupDetail(null); }} sx={{ color: NAV_BG }}>
                            <ArrowBackIcon />
                        </IconButton>
                    )}
                    <MailIcon sx={{ color: NAV_BG, fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG }}>
                        {selectedGroup ? (selectedGroup.title || t('messages.no_subject')) : t('messages.title')}
                    </Typography>
                    {selectedGroup?.is_invitation && (
                        <Chip size="small" icon={<EventIcon sx={{ fontSize: '14px !important' }} />} label={t('messages.invitation')}
                            sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', height: 22, background: 'rgba(245,158,11,0.12)', color: '#92400e', border: '1px solid rgba(245,158,11,0.3)' }} />
                    )}
                </Box>
                {!selectedGroup && (
                    <Button variant="contained" startIcon={<SendIcon />} onClick={() => setComposeOpen(true)}
                        sx={{ background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG, fontFamily: '"Outfit", sans-serif', borderRadius: '8px', textTransform: 'none', fontWeight: 600, boxShadow: `0 4px 14px ${AMBER_GLOW}`, '&:hover': { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)` } }}>
                        {t('messages.compose')}
                    </Button>
                )}
            </Box>

            {/* Tabs: Sent / Received */}
            {!selectedGroup && (
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600 }, '& .Mui-selected': { color: NAV_BG }, '& .MuiTabs-indicator': { backgroundColor: NAV_BG } }}>
                    <Tab icon={
                        <Badge badgeContent={unreadReceivedCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}>
                            <InboxIcon sx={{ fontSize: 16 }} />
                        </Badge>
                    } iconPosition="start" label={t('messages.tab_received') || 'Received'} />
                    <Tab icon={<SendIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={t('messages.tab_sent') || 'Sent'} />
                </Tabs>
            )}

            {/* Message Group List (Sent) */}
            {!selectedGroup && tab === 1 && (
                <>
                {groups.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton size="small" onClick={toggleSelectAllSent} sx={{ color: NAV_BG }}>
                                {selectedSent.size === groups.length
                                    ? <CheckBoxIcon fontSize="small" />
                                    : <CheckBoxOutlineBlankIcon fontSize="small" />}
                            </IconButton>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#64748b' }}>
                                {selectedSent.size > 0 ? t('common.n_selected', { count: selectedSent.size }) : t('common.select_all')}
                            </Typography>
                        </Box>
                        {selectedSent.size > 0 && (
                            <Button variant="outlined" size="small" color="error" startIcon={deletingSent ? <CircularProgress size={12} color="inherit" /> : <DeleteIcon sx={{ fontSize: 16 }} />}
                                onClick={handleDeleteSent} disabled={deletingSent}
                                sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', borderRadius: '8px', fontSize: '0.8rem' }}>
                                {t('common.delete_n', { count: selectedSent.size })}
                            </Button>
                        )}
                    </Box>
                )}
                <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                    {groups.length === 0 ? (
                        <EmptyState icon={MailIcon} message={t('messages.no_messages')} />
                    ) : (
                        <List disablePadding>
                            {groups.map((g, idx) => (
                                <React.Fragment key={g.group_id}>
                                    {idx > 0 && <Divider />}
                                    <ListItem button onClick={() => openGroup(g)}
                                        sx={{ py: 2, px: 2.5, background: selectedSent.has(g.group_id) ? 'rgba(15,45,94,0.06)' : 'transparent', '&:hover': { background: 'rgba(15,45,94,0.04)' }, cursor: 'pointer' }}>
                                        <Box onClick={e => toggleSelectSent(g.group_id, e)} sx={{ mr: 1, display: 'flex', alignItems: 'center', color: NAV_BG }}>
                                            {selectedSent.has(g.group_id)
                                                ? <CheckBoxIcon fontSize="small" />
                                                : <CheckBoxOutlineBlankIcon fontSize="small" sx={{ color: '#94a3b8' }} />}
                                        </Box>
                                        <ListItemIcon sx={{ minWidth: 44 }}>
                                            <Avatar sx={{ width: 36, height: 36, background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG }}>
                                                {g.is_invitation ? <EventIcon sx={{ fontSize: 18 }} /> : <MailIcon sx={{ fontSize: 18 }} />}
                                            </Avatar>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, color: NAV_BG, fontSize: '0.95rem' }}>
                                                        {g.title || t('messages.no_subject')}
                                                    </Typography>
                                                    {g.is_invitation && (
                                                        <Chip size="small" label={t('messages.invitation')}
                                                            sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.68rem', height: 18, background: 'rgba(245,158,11,0.12)', color: '#92400e' }} />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                                    <Typography variant="caption" sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b' }}>
                                                        {fmtDate(g.sent_ts)}
                                                    </Typography>
                                                    {g.is_invitation && (g.rsvp_yes + g.rsvp_no + g.rsvp_maybe > 0) && (
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            {g.rsvp_yes  > 0 && <Chip size="small" label={`✓ ${g.rsvp_yes}`}  sx={{ height: 16, fontSize: '0.65rem', background: RSVP_COLORS.yes.bg,   color: RSVP_COLORS.yes.text,   fontFamily: '"Outfit", sans-serif' }} />}
                                                            {g.rsvp_no   > 0 && <Chip size="small" label={`✗ ${g.rsvp_no}`}   sx={{ height: 16, fontSize: '0.65rem', background: RSVP_COLORS.no.bg,    color: RSVP_COLORS.no.text,    fontFamily: '"Outfit", sans-serif' }} />}
                                                            {g.rsvp_maybe > 0 && <Chip size="small" label={`? ${g.rsvp_maybe}`} sx={{ height: 16, fontSize: '0.65rem', background: RSVP_COLORS.maybe.bg, color: RSVP_COLORS.maybe.text, fontFamily: '"Outfit", sans-serif' }} />}
                                                        </Box>
                                                    )}
                                                </Box>
                                            }
                                        />
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, ml: 2 }}>
                                            <Chip size="small" icon={<DraftsIcon sx={{ fontSize: '14px !important' }} />}
                                                label={`${g.read_count}/${g.total} ${t('messages.read')}`}
                                                sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', height: 22,
                                                    background: g.read_count === g.total ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.12)',
                                                    color: g.read_count === g.total ? '#15803d' : '#92400e',
                                                    border: `1px solid ${g.read_count === g.total ? 'rgba(34,197,94,0.25)' : 'rgba(245,158,11,0.25)'}` }} />
                                            <Typography variant="caption" sx={{ fontFamily: '"Outfit", sans-serif', color: '#94a3b8', fontSize: '0.72rem' }}>
                                                {g.total} {t('messages.recipients')}
                                            </Typography>
                                        </Box>
                                    </ListItem>
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </Paper>
                </>
            )}

            {/* Received Messages List */}
            {!selectedGroup && tab === 0 && (
                <>
                    {received.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton size="small" onClick={toggleSelectAllReceived} sx={{ color: NAV_BG }}>
                                    {selectedReceived.size === received.length
                                        ? <CheckBoxIcon fontSize="small" />
                                        : <CheckBoxOutlineBlankIcon fontSize="small" />}
                                </IconButton>
                                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#64748b' }}>
                                    {selectedReceived.size > 0 ? t('common.n_selected', { count: selectedReceived.size }) : t('common.select_all')}
                                </Typography>
                            </Box>
                            {selectedReceived.size > 0 && (
                                <Button variant="outlined" size="small" color="error" startIcon={deleting ? <CircularProgress size={12} color="inherit" /> : <DeleteIcon sx={{ fontSize: 16 }} />}
                                    onClick={handleDeleteReceived} disabled={deleting}
                                    sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', borderRadius: '8px', fontSize: '0.8rem' }}>
                                    {t('common.delete_n', { count: selectedReceived.size })}
                                </Button>
                            )}
                        </Box>
                    )}
                    <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                        {received.length === 0 ? (
                            <EmptyState icon={InboxIcon} message={t('messages.no_messages_received')} />
                        ) : (
                            <List disablePadding>
                                {received.map((msg, idx) => (
                                    <React.Fragment key={msg.message_id}>
                                        {idx > 0 && <Divider />}
                                        <ListItem button onClick={() => openReceivedMessage(msg)}
                                            sx={{ py: 2, px: 2.5, cursor: 'pointer', background: selectedReceived.has(msg.message_id) ? 'rgba(15,45,94,0.06)' : msg.read ? 'transparent' : 'rgba(15,45,94,0.03)', '&:hover': { background: 'rgba(15,45,94,0.06)' } }}>
                                            <Box onClick={e => toggleSelectReceived(msg.message_id, e)} sx={{ mr: 1, display: 'flex', alignItems: 'center', color: NAV_BG }}>
                                                {selectedReceived.has(msg.message_id)
                                                    ? <CheckBoxIcon fontSize="small" />
                                                    : <CheckBoxOutlineBlankIcon fontSize="small" sx={{ color: '#94a3b8' }} />}
                                            </Box>
                                            <ListItemIcon sx={{ minWidth: 44 }}>
                                                <Avatar sx={{ width: 36, height: 36, background: msg.read ? '#e2e8f0' : `linear-gradient(135deg, ${NAV_BG} 0%, #163571 100%)`, color: msg.read ? '#64748b' : '#fff' }}>
                                                    <PersonIcon sx={{ fontSize: 18 }} />
                                                </Avatar>
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {!msg.read && <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: NAV_BG, flexShrink: 0 }} />}
                                                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: msg.read ? 500 : 700, color: NAV_BG, fontSize: '0.95rem' }}>
                                                            {msg.title || t('messages.no_subject')}
                                                        </Typography>
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.25 }}>
                                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                                            <Typography variant="caption" sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', fontWeight: 600 }}>
                                                                {msg.from_name || msg.from_email}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ fontFamily: '"Outfit", sans-serif', color: '#94a3b8' }}>
                                                                · {fmtDate(msg.sent_ts)}
                                                            </Typography>
                                                        </Box>
                                                        {msg.from_children && msg.from_children.length > 0 && (
                                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                                {msg.from_children.map(c => (
                                                                    <Chip key={c.id} size="small" label={`${c.firstname} ${c.lastname}`}
                                                                        sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.65rem', height: 18, background: 'rgba(15,45,94,0.07)', color: NAV_BG, border: '1px solid rgba(15,45,94,0.15)' }} />
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                }
                                            />
                                            <Box sx={{ ml: 2 }}>
                                                {msg.read
                                                    ? <DraftsIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                                                    : <MailIcon   sx={{ fontSize: 20, color: NAV_BG }} />}
                                            </Box>
                                        </ListItem>
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </Paper>
                </>
            )}

            {/* Received Message Detail Dialog */}
            <Dialog open={!!openedMessage} onClose={() => setOpenedMessage(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                {openedMessage && (
                    <>
                        <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG }}>
                            {openedMessage.title || t('messages.no_subject')}
                        </DialogTitle>
                        <DialogContent>
                            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Chip size="small" icon={<PersonIcon sx={{ fontSize: '14px !important' }} />}
                                    label={openedMessage.from_name || openedMessage.from_email}
                                    sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', height: 22, background: 'rgba(15,45,94,0.08)', color: NAV_BG }} />
                                <Typography variant="caption" sx={{ fontFamily: '"Outfit", sans-serif', color: '#94a3b8' }}>
                                    {fmtDate(openedMessage.sent_ts)}
                                </Typography>
                            </Box>
                            {openedMessage.from_children && openedMessage.from_children.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
                                    {openedMessage.from_children.map(c => (
                                        <Chip key={c.id} size="small" label={`${c.firstname} ${c.lastname}`}
                                            sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', height: 22, background: 'rgba(15,45,94,0.07)', color: NAV_BG, border: '1px solid rgba(15,45,94,0.15)' }} />
                                    ))}
                                </Box>
                            )}
                            <Box sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.95rem', lineHeight: 1.7, color: '#1e293b', '& ul': { pl: 2.5 }, '& li': { mb: 0.25 } }}
                                dangerouslySetInnerHTML={{ __html: openedMessage.text }} />
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button onClick={() => setOpenedMessage(null)} sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', color: '#64748b' }}>
                                {t('common.close')}
                            </Button>
                            <Button
                                variant="contained" startIcon={<ReplyIcon sx={{ fontSize: 16 }} />}
                                onClick={() => handleReply(openedMessage)}
                                sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600, borderRadius: '8px', background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG, '&:hover': { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)` } }}
                            >
                                {t('messages.reply')}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Message Detail */}
            {selectedGroup && (
                <Box>
                    {detailLoading ? (
                        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
                    ) : groupDetail && (
                        <>
                            {/* Message body */}
                            <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: 3, mb: 3 }}>
                                <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Typography variant="caption" sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b' }}>
                                        {fmtDate(groupDetail.sent_ts)}
                                    </Typography>
                                    <Chip size="small"
                                        label={`${groupDetail.recipients.filter(r => r.read).length}/${groupDetail.recipients.length} ${t('messages.read')}`}
                                        sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', background: 'rgba(15,45,94,0.08)', color: NAV_BG, height: 20 }} />
                                    {groupDetail.is_invitation && (() => {
                                        const yes   = groupDetail.recipients.filter(r => r.rsvp === 'yes').length;
                                        const no    = groupDetail.recipients.filter(r => r.rsvp === 'no').length;
                                        const maybe = groupDetail.recipients.filter(r => r.rsvp === 'maybe').length;
                                        return (
                                            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                                                <Chip size="small" icon={<CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />} label={`${yes} ${t('messages.rsvp_yes')}`}   sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', height: 22, ...RSVP_COLORS.yes   }} />
                                                <Chip size="small" icon={<CancelOutlinedIcon    sx={{ fontSize: '14px !important' }} />} label={`${no} ${t('messages.rsvp_no')}`}    sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', height: 22, ...RSVP_COLORS.no    }} />
                                                <Chip size="small" icon={<HelpOutlineIcon       sx={{ fontSize: '14px !important' }} />} label={`${maybe} ${t('messages.rsvp_maybe')}`} sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', height: 22, ...RSVP_COLORS.maybe }} />
                                            </Box>
                                        );
                                    })()}
                                </Box>
                                <Box sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.95rem', lineHeight: 1.7, color: '#1e293b', '& ul': { pl: 2.5 }, '& li': { mb: 0.25 } }}
                                    dangerouslySetInnerHTML={{ __html: groupDetail.text }} />
                            </Paper>

                            {/* Children RSVP — only for invitations */}
                            {groupDetail.is_invitation && groupDetail.children_rsvp && groupDetail.children_rsvp.length > 0 && (() => {
                                const attending = groupDetail.children_rsvp.filter(c => c.rsvp === 'yes').length;
                                const maybe     = groupDetail.children_rsvp.filter(c => c.rsvp === 'maybe').length;
                                const declined  = groupDetail.children_rsvp.filter(c => c.rsvp === 'no').length;
                                const pending   = groupDetail.children_rsvp.filter(c => !c.rsvp).length;
                                return (
                                    <Box sx={{ mb: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                            <ChildCareIcon sx={{ fontSize: 18, color: NAV_BG }} />
                                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, color: NAV_BG, fontSize: '0.9rem' }}>
                                                {t('messages.children_attendance')} ({groupDetail.children_rsvp.length})
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                                                {attending > 0 && <Chip size="small" icon={<CheckCircleOutlineIcon sx={{ fontSize: '13px !important' }} />} label={attending} sx={{ height: 20, fontSize: '0.68rem', ...RSVP_COLORS.yes, fontFamily: '"Outfit", sans-serif' }} />}
                                                {maybe     > 0 && <Chip size="small" icon={<HelpOutlineIcon       sx={{ fontSize: '13px !important' }} />} label={maybe}     sx={{ height: 20, fontSize: '0.68rem', ...RSVP_COLORS.maybe, fontFamily: '"Outfit", sans-serif' }} />}
                                                {declined  > 0 && <Chip size="small" icon={<CancelOutlinedIcon    sx={{ fontSize: '13px !important' }} />} label={declined}  sx={{ height: 20, fontSize: '0.68rem', ...RSVP_COLORS.no, fontFamily: '"Outfit", sans-serif' }} />}
                                                {pending   > 0 && <Chip size="small" label={`${pending} ?`} sx={{ height: 20, fontSize: '0.68rem', ...RSVP_COLORS.null, fontFamily: '"Outfit", sans-serif' }} />}
                                            </Box>
                                        </Box>
                                        <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                                            <List disablePadding>
                                                {groupDetail.children_rsvp.map((child, idx) => (
                                                    <React.Fragment key={child.child_id}>
                                                        {idx > 0 && <Divider />}
                                                        <ListItem sx={{ py: 1.25, px: 2.5 }}>
                                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                                <ChildCareIcon sx={{ fontSize: 20, color: child.rsvp === 'yes' ? '#22c55e' : child.rsvp === 'no' ? '#ef4444' : child.rsvp === 'maybe' ? AMBER : '#94a3b8' }} />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.9rem', fontWeight: 500, color: '#1e293b' }}>{child.firstname} {child.lastname}</Typography>}
                                                                secondary={
                                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                                                                        {child.class_name && <Chip size="small" label={child.class_name} sx={{ height: 16, fontSize: '0.63rem', background: 'rgba(15,45,94,0.07)', color: NAV_BG, fontFamily: '"Outfit", sans-serif' }} />}
                                                                        {child.department_name && <Chip size="small" label={child.department_name} sx={{ height: 16, fontSize: '0.63rem', background: 'rgba(15,45,94,0.05)', color: '#475569', fontFamily: '"Outfit", sans-serif' }} />}
                                                                        {child.parents.map((p, pi) => (
                                                                            <Chip key={pi} size="small"
                                                                                label={`${p.name}${p.rsvp ? `: ${p.rsvp}` : ''}`}
                                                                                sx={{ height: 16, fontSize: '0.63rem', background: p.rsvp === 'yes' ? RSVP_COLORS.yes.bg : p.rsvp === 'no' ? RSVP_COLORS.no.bg : p.rsvp === 'maybe' ? RSVP_COLORS.maybe.bg : RSVP_COLORS.null.bg, color: p.rsvp === 'yes' ? RSVP_COLORS.yes.text : p.rsvp === 'no' ? RSVP_COLORS.no.text : p.rsvp === 'maybe' ? RSVP_COLORS.maybe.text : RSVP_COLORS.null.text, fontFamily: '"Outfit", sans-serif' }} />
                                                                        ))}
                                                                    </Box>
                                                                }
                                                            />
                                                            <RsvpChip rsvp={child.rsvp} />
                                                        </ListItem>
                                                    </React.Fragment>
                                                ))}
                                            </List>
                                        </Paper>
                                    </Box>
                                );
                            })()}

                            {/* Recipients */}
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, color: NAV_BG, mb: 1.5, fontSize: '0.9rem' }}>
                                {t('messages.recipients_list')} ({groupDetail.recipients.length})
                            </Typography>
                            <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                                <List disablePadding>
                                    {groupDetail.recipients.map((r, idx) => (
                                        <React.Fragment key={r.message_id}>
                                            {idx > 0 && <Divider />}
                                            <ListItem sx={{ py: 1.25, px: 2.5 }}>
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    {r.read
                                                        ? <DraftsIcon sx={{ fontSize: 20, color: '#22c55e' }} />
                                                        : <MailIcon   sx={{ fontSize: 20, color: '#94a3b8' }} />}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.9rem', fontWeight: 500, color: '#1e293b' }}>{r.firstname} {r.lastname}</Typography>}
                                                    secondary={
                                                        <Box>
                                                            <Typography variant="caption" sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b' }}>{r.email}</Typography>
                                                            {r.children && r.children.length > 0 && (
                                                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                                                                    {r.children.map(c => (
                                                                        <Chip key={c.id} size="small" label={`${c.firstname} ${c.lastname}`}
                                                                            sx={{ height: 16, fontSize: '0.63rem', background: 'rgba(15,45,94,0.07)', color: NAV_BG, fontFamily: '"Outfit", sans-serif' }} />
                                                                    ))}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                                    {r.read ? (
                                                        <>
                                                            <Chip size="small" label={t('messages.read_label')}
                                                                sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', background: 'rgba(34,197,94,0.12)', color: '#15803d', border: '1px solid rgba(34,197,94,0.25)', height: 22 }} />
                                                            {r.read_ts && (
                                                                <Typography variant="caption" sx={{ fontFamily: '"Outfit", sans-serif', color: '#94a3b8', fontSize: '0.68rem' }}>
                                                                    {fmtDate(r.read_ts)}
                                                                </Typography>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <Chip size="small" label={t('messages.unread_label')}
                                                            sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.72rem', background: 'rgba(148,163,184,0.12)', color: '#64748b', border: '1px solid rgba(148,163,184,0.25)', height: 22 }} />
                                                    )}
                                                    {groupDetail.is_invitation && <RsvpChip rsvp={r.rsvp} />}
                                                </Box>
                                            </ListItem>
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Paper>
                        </>
                    )}
                </Box>
            )}

            {/* Compose Dialog */}
            <Dialog open={composeOpen} onClose={() => { setComposeOpen(false); resetCompose(); }}
                maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG }}>
                    {t('messages.compose')}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '12px !important' }}>

                    {/* Target type */}
                    <FormControl size="small" fullWidth>
                        <InputLabel sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem' }}>{t('messages.send_to')}</InputLabel>
                        <Select value={targetType} label={t('messages.send_to')} onChange={e => handleTargetTypeChange(e.target.value)}
                            sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', borderRadius: '8px' }}>
                            <MuiMenuItem value="all"        sx={{ fontFamily: '"Outfit", sans-serif' }}><Box sx={{ display: 'flex', alignItems: 'center' }}><GroupIcon        sx={{ fontSize: 16, mr: 0.5 }} />{t('messages.target_all')}</Box></MuiMenuItem>
                            <MuiMenuItem value="all_routes" sx={{ fontFamily: '"Outfit", sans-serif' }}><Box sx={{ display: 'flex', alignItems: 'center' }}><DirectionsBusIcon sx={{ fontSize: 16, mr: 0.5 }} />{t('messages.target_all_routes')}</Box></MuiMenuItem>
                            <MuiMenuItem value="schoolclass" sx={{ fontFamily: '"Outfit", sans-serif' }}><Box sx={{ display: 'flex', alignItems: 'center' }}><SchoolIcon       sx={{ fontSize: 16, mr: 0.5 }} />{t('messages.target_class')}</Box></MuiMenuItem>
                            <MuiMenuItem value="department" sx={{ fontFamily: '"Outfit", sans-serif' }}><Box sx={{ display: 'flex', alignItems: 'center' }}><ClassIcon         sx={{ fontSize: 16, mr: 0.5 }} />{t('messages.target_department')}</Box></MuiMenuItem>
                            <MuiMenuItem value="route"      sx={{ fontFamily: '"Outfit", sans-serif' }}><Box sx={{ display: 'flex', alignItems: 'center' }}><DirectionsBusIcon sx={{ fontSize: 16, mr: 0.5 }} />{t('messages.target_route')}</Box></MuiMenuItem>
                            <MuiMenuItem value="children"   sx={{ fontFamily: '"Outfit", sans-serif' }}><Box sx={{ display: 'flex', alignItems: 'center' }}><PersonIcon        sx={{ fontSize: 16, mr: 0.5 }} />{t('messages.target_children')}</Box></MuiMenuItem>
                        </Select>
                    </FormControl>

                    {/* Class picker (for class or department target) */}
                    {(targetType === 'schoolclass' || targetType === 'department') && (
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem' }}>{t('messages.pick_class')}</InputLabel>
                            <Select value={selectedClass} label={t('messages.pick_class')}
                                onChange={e => { setSelectedClass(e.target.value); setTargetId(targetType === 'schoolclass' ? e.target.value : ''); }}
                                sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', borderRadius: '8px' }}>
                                {classes.map(c => (
                                    <MuiMenuItem key={c.id} value={String(c.id)} sx={{ fontFamily: '"Outfit", sans-serif' }}>{c.name}</MuiMenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Department picker */}
                    {targetType === 'department' && selectedClass && (
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem' }}>{t('messages.pick_department')}</InputLabel>
                            <Select value={targetId} label={t('messages.pick_department')} onChange={e => setTargetId(e.target.value)}
                                sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', borderRadius: '8px' }}>
                                {deptOptions.map(d => (
                                    <MuiMenuItem key={d.id} value={String(d.id)} sx={{ fontFamily: '"Outfit", sans-serif' }}>{d.name}</MuiMenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Route picker */}
                    {targetType === 'route' && (
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem' }}>{t('messages.pick_route')}</InputLabel>
                            <Select value={targetId} label={t('messages.pick_route')} onChange={e => setTargetId(e.target.value)}
                                sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', borderRadius: '8px' }}>
                                {routes.map(r => (
                                    <MuiMenuItem key={r.id} value={String(r.id)} sx={{ fontFamily: '"Outfit", sans-serif' }}>{r.name}</MuiMenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Multi-child picker */}
                    {targetType === 'children' && (
                        <Box>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#64748b', mb: 0.75 }}>
                                {t('messages.pick_children')}
                                {targetChildIds.length > 0 && (
                                    <Box component="span" sx={{ ml: 1, color: NAV_BG, fontWeight: 600 }}>
                                        ({targetChildIds.length} {t('messages.selected')})
                                    </Box>
                                )}
                            </Typography>
                            <MultiChildPicker classes={classes} selectedIds={targetChildIds} onChange={setTargetChildIds} />
                        </Box>
                    )}

                    {/* Subject */}
                    <TextField label={t('messages.subject')} fullWidth size="small" value={composeTitle}
                        onChange={e => setComposeTitle(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: AMBER }, '& .MuiInputLabel-root.Mui-focused': { color: AMBER } }}
                        inputProps={{ style: { fontFamily: '"Outfit", sans-serif' } }} />

                    {/* Rich text body */}
                    <Box>
                        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#64748b', mb: 0.5 }}>
                            {t('messages.message_body')}
                        </Typography>
                        <RichTextEditor value={composeText} onChange={setComposeText} />
                    </Box>

                    {/* RSVP / Invitation toggle */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 0.5, py: 1, borderRadius: '10px', border: isInvitation ? `1px solid rgba(245,158,11,0.4)` : '1px solid transparent', background: isInvitation ? 'rgba(245,158,11,0.04)' : 'transparent', transition: 'all 0.2s' }}>
                        <EventIcon sx={{ fontSize: 20, color: isInvitation ? AMBER : '#94a3b8' }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', fontWeight: 600, color: isInvitation ? '#92400e' : '#64748b' }}>
                                {t('messages.invitation_toggle')}
                            </Typography>
                            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: '#94a3b8' }}>
                                {t('messages.invitation_hint')}
                            </Typography>
                        </Box>
                        <Switch checked={isInvitation} onChange={e => setIsInvitation(e.target.checked)}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: AMBER }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: AMBER } }} />
                    </Box>

                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => { setComposeOpen(false); resetCompose(); }}
                        sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', color: '#64748b' }}>
                        {t('common.cancel')}
                    </Button>
                    <Button variant="contained" onClick={handleSend} disabled={sendDisabled}
                        startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
                        sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600, borderRadius: '8px', background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG, '&:hover': { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)` } }}>
                        {sending ? t('messages.sending') : t('messages.send')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
